import { formatLength, parseLength, type LengthUnit, type Rect } from '@flobi/artwork-core';
import type { IconName } from '../chrome/icons';

/**
 * What an effector says to the person using it, kept apart from the tool that draws it.
 *
 * The engine has three kinds and five fields, which are Cinema 4D's words and a good way
 * to build the arithmetic. They are not a good way to start: nobody opens an array
 * thinking "I want a plain effector with a radial field". They think "make the ones in the
 * middle bigger". So the effector is started from what it DOES (`RECIPES`), and only then
 * shown as the numbers it is made of — every one of them, named, all at once — and the two
 * questions the engine asks (how the amounts are handed out, and where) as pop-ups that
 * say what they are asking.
 */

export type EffectorKind = 'plain' | 'random' | 'step';
export type FieldKind = 'none' | 'linear' | 'radial' | 'box' | 'shape';

/** How the amounts are handed out across the copies. */
export const APPLY: { id: EffectorKind; label: string; icon: IconName; title: string }[] = [
  { id: 'plain', label: 'Evenly', icon: 'effector-plain', title: 'Evenly — every copy the effect reaches gets the same amount' },
  { id: 'random', label: 'At random', icon: 'effector-random', title: 'At random — each copy gets its own amount, the same ones every time the file is opened' },
  { id: 'step', label: 'Gradually', icon: 'effector-step', title: 'Gradually — none at the first copy, growing along the numbering to all of it at the last' },
];

/** Where in the array the effect is felt. */
export const WHERE: { id: FieldKind; label: string; icon: IconName; title: string }[] = [
  { id: 'none', label: 'Everywhere', icon: 'field-none', title: 'Everywhere — every copy, wherever it is' },
  { id: 'radial', label: 'Around a spot', icon: 'field-radial', title: 'Around a spot — most in the middle, fading out in a circle' },
  { id: 'box', label: 'In a box', icon: 'field-box', title: 'In a box — most in the middle, fading out in a square' },
  { id: 'linear', label: 'From a side', icon: 'field-linear', title: 'From a side — most at one edge, fading out towards the other' },
  { id: 'shape', label: 'Inside a shape', icon: 'select-lasso', title: 'Inside a shape — a shape you draw is the area; click one to choose it' },
];

export const applyOf = (kind: string | undefined) => APPLY.find((one) => one.id === kind) ?? APPLY[0]!;
export const whereOf = (field: string | undefined) => WHERE.find((one) => one.id === field) ?? WHERE[0]!;

/** The first number in typed text — "45°" is 45 — or null when there is none. */
export function numberIn(text: string): number | null {
  const found = /[-+]?(?:\d+\.?\d*|\.\d+)/.exec(text);
  return found ? Number(found[0]) : null;
}

/** A typed percentage as a share: "40%" and "40" are both 0.4. */
export function percentIn(text: string): number | null {
  const value = numberIn(text);
  return value === null ? null : value / 100;
}

/**
 * What an effector changes, one number each.
 *
 * Every channel means "no change" at nought, so all of them are always applied and only the
 * ones that are set do anything. They are ALL on the strip, named, because the picker that
 * used to choose which one a single slider was turning also hid which ones were set — the
 * only way to find out what an effector did was to click through five marks.
 */
export const CHANNELS: {
  id: 'moveX' | 'moveY' | 'rotate' | 'scale' | 'shade';
  field: 'offsetX' | 'offsetY' | 'rotate' | 'scale' | 'shade';
  /** What is written in front of the number. */
  label: string;
  /** "Name — what it means", for the help tag. */
  title: string;
  min: number;
  max: number;
  step: number;
  round: (value: number) => number;
  format: (value: number, unit: LengthUnit) => string;
  /** And typed text read back the way `format` says it (cloner-F13). */
  parse: (text: string, unit: LengthUnit) => number | null;
}[] = [
  {
    id: 'moveX',
    field: 'offsetX',
    label: 'Move X',
    title: 'Move X — how far a copy slides left or right, where the effect is strongest',
    min: -600,
    max: 600,
    step: 1,
    round: Math.round,
    format: (value, unit) => formatLength(value, unit, { compact: true }),
    parse: (text, unit) => parseLength(text, unit),
  },
  {
    id: 'moveY',
    field: 'offsetY',
    label: 'Move Y',
    title: 'Move Y — how far a copy slides up or down, where the effect is strongest',
    min: -600,
    max: 600,
    step: 1,
    round: Math.round,
    format: (value, unit) => formatLength(value, unit, { compact: true }),
    parse: (text, unit) => parseLength(text, unit),
  },
  {
    id: 'rotate',
    field: 'rotate',
    label: 'Turn',
    title: 'Turn — how far a copy is turned, where the effect is strongest',
    min: -180,
    max: 180,
    step: 1,
    round: Math.round,
    format: (value) => `${Math.round(value)}°`,
    parse: (text) => numberIn(text),
  },
  {
    id: 'scale',
    field: 'scale',
    label: 'Size',
    title: 'Size — how much bigger or smaller a copy is, where the effect is strongest',
    min: -1,
    max: 2,
    step: 0.05,
    round: (value) => Number(value.toFixed(2)),
    format: (value) => `${value > 0 ? '+' : ''}${Math.round(value * 100)}%`,
    parse: (text) => percentIn(text),
  },
  {
    id: 'shade',
    field: 'shade',
    label: 'Shade',
    title: 'Shade — how much lighter or darker a copy is, where the effect is strongest. Plus is lighter, minus is darker',
    min: -1,
    max: 1,
    step: 0.05,
    round: (value) => Number(value.toFixed(2)),
    // Said in words, because a bare "-40%" does not say which way is darker.
    format: (value) => (value === 0 ? 'none' : `${Math.round(Math.abs(value) * 100)}% ${value > 0 ? 'lighter' : 'darker'}`),
    // Read the way it is said: "40% darker" and "-40%" are both minus forty, "none" is nought.
    parse: (text) => {
      if (/^\s*(none)?\s*$/i.test(text)) return 0;
      const share = percentIn(text);
      if (share === null) return null;
      if (/dark/i.test(text)) return -Math.abs(share);
      if (/light/i.test(text)) return Math.abs(share);
      return share;
    },
  },
];

/**
 * A way to start.
 *
 * Named for what it does to the page, in one word, so the menu that offers them is a list
 * of outcomes rather than of settings. Each is only a starting point: the effector it makes
 * is the ordinary numbers, and changing any of them makes it something else with no
 * ceremony — which is also why an effector does not remember which recipe it came from.
 */
export interface Recipe {
  readonly id: 'scatter' | 'grow' | 'twist' | 'fade' | 'magnify';
  readonly name: string;
  readonly icon: IconName;
  /** "Name — what it does", for the guide card and the help tag. */
  readonly title: string;
  /** What it sets, given how far apart the copies are. */
  make(spacing: number): RecipeProps;
}

export interface RecipeProps {
  readonly kind: EffectorKind;
  readonly field: FieldKind;
  readonly offsetX: number;
  readonly offsetY: number;
  readonly rotate: number;
  readonly scale: number;
  readonly shade: number;
}

const still = { offsetX: 0, offsetY: 0, rotate: 0, scale: 0, shade: 0 };

export const RECIPES: readonly Recipe[] = [
  {
    id: 'scatter',
    name: 'Scatter',
    icon: 'effector-random',
    title: 'Scatter — every copy knocked a little off its place, turned and resized, each its own way',
    // A share of the gap between copies: a scatter that ignores the gap is a shuffle of
    // one pixel on a wide grid and a heap on a tight one.
    make: (spacing) => ({
      ...still,
      kind: 'random',
      field: 'none',
      offsetX: Math.round(spacing * 0.18),
      offsetY: Math.round(spacing * 0.18),
      rotate: 18,
      scale: 0.25,
    }),
  },
  {
    id: 'grow',
    name: 'Grow',
    icon: 'expand',
    title: 'Grow — each copy a little bigger than the one before it',
    make: () => ({ ...still, kind: 'step', field: 'none', scale: 0.5 }),
  },
  {
    id: 'twist',
    name: 'Twist',
    icon: 'rotate',
    title: 'Twist — each copy turned a little further than the one before it',
    make: () => ({ ...still, kind: 'step', field: 'none', rotate: 90 }),
  },
  {
    id: 'fade',
    name: 'Fade',
    icon: 'shade',
    title: 'Fade — each copy a little lighter than the one before it',
    make: () => ({ ...still, kind: 'step', field: 'none', shade: 0.7 }),
  },
  {
    id: 'magnify',
    name: 'Magnify',
    icon: 'field-radial',
    title: 'Magnify — the copies in the middle swell, fading back to their own size at the edge',
    make: () => ({ ...still, kind: 'plain', field: 'radial', scale: 0.4 }),
  },
];

/**
 * How far apart the copies are, as one number, whatever the arrangement.
 *
 * A grid has two steps and a ring has none, and an effector has to be sized for all of
 * them — so it is read off the array as it stands: its box, shared out among its copies,
 * is the room each one has.
 */
export function spacingOf(box: Rect, copies: number): number {
  const each = (Math.max(1, box.width) * Math.max(1, box.height)) / Math.max(1, copies);
  return Math.max(8, Math.round(Math.sqrt(each)));
}
